// Types ----------------------------------------------------------
export interface RoomTypeData {
  id: string;
  name: string;
  occupancy?: number;
  rate?: number;
  percentage?: number;
  fixed?: number;
  rateFloor?: number | string;
  rateCeiling?: number | string;
  isFloorOverridden?: boolean;
  isCeilingOverridden?: boolean;
  isPriceDifferentialOverridden?: boolean;
}

export interface PriceEditorData {
  baseRoomTypeId: string;
  baseRoomTypeName: string;
  baseRoomTypeRate: number;
  rateFloor?: number | string;
  rateCeiling?: number | string;
  roomTypes: RoomTypeData[];
}

// Calculations ---------------------------------------------------
/** New base price given an entered percentage (relative) or dollar delta (fixed). */
export function calculateNewPrice(
  newPriceOrPercentage: string,
  basePrice: string | number,
  relativePrice: boolean
): string {
  const delta = Number(newPriceOrPercentage);
  const base = Number(basePrice);
  if (isNaN(delta) || isNaN(base)) return "";
  if (relativePrice) return ((base * delta) / 100 + base).toFixed(0);
  return (delta + base).toFixed(0);
}

/** Derived room's resulting price from the (already-final) base price + its differential. */
export function computeDerivedNewPrice(
  basePrice: number,
  differential: number,
  relativePrice: boolean
): string {
  const base = Number(basePrice) || 0;
  if (relativePrice) {
    return String(Math.round((differential / 100) * base) + base);
  }
  return String(differential + base);
}

/** Cascade a base floor/ceiling to a derived room using its differential. "" if base is 0. */
export function cascadeConstraint(
  baseConstraint: number,
  differential: number,
  relativePrice: boolean
): string {
  const base = Number(baseConstraint) || 0;
  if (base <= 0) return "";
  const calc = relativePrice
    ? base + (base * Number(differential || 0)) / 100
    : base + Number(differential || 0);
  return calc > 0 ? String(Math.round(calc)) : "";
}

/** Returns an error message, or "" if valid. Mirrors the modal's debounced validator. */
export function validatePrice(price: string, floor: number, ceiling: number): string {
  const n = Number(price);
  if (!price || price === "0") return "";
  if (floor > 0 && n < floor) return `Price $${n} is below rate floor`;
  if (ceiling > 0 && n > ceiling) return `Price $${n} is above rate ceiling`;
  return "";
}

// Save payload builder -------------------------------------------
export interface PriceFormRoom extends RoomTypeData {
  newPrice: string;
  newFixedPrice?: string;
  newRelativePrice?: string;
}

export interface PriceForm {
  relativePrice: boolean;
  bulk: boolean;
  rateFloor: string;
  rateCeiling: string;
  baseRoomTypeNewPrice: string;
  date?: string;
  startDate: string;
  endDate: string;
  excludedDates: string[];
  roomTypes: PriceFormRoom[];
}

export interface SavePayload {
  startDate: string;
  endDate?: string;
  excludedDates?: string[];
  roomTypePrices: Array<{ roomTypeId: string; price: string }>;
  roomTypeFloorsCeilings: Record<
    string,
    { rateFloor: number | null; rateCeiling: number | null }
  >;
  isOverride: boolean;
  fixedByRoom: Record<string, string>;
  percentagesByRoom: Record<string, string>;
  overridedByFixed: boolean;
  currentPricesByRoom: Record<string, number>;
}

export interface SingleFloorCeiling {
  date?: string;
  roomTypeFloorsCeilings: SavePayload["roomTypeFloorsCeilings"];
}

export interface BulkFloorCeiling {
  startDate: string;
  endDate: string;
  roomTypeFloorsCeilings: SavePayload["roomTypeFloorsCeilings"];
  excludedDates: string[];
}

export function buildSavePayload(
  form: PriceForm,
  data: PriceEditorData,
  // Single-day fallback date. `form.date` is only set when the desktop modal's
  // date picker is used; the mobile editor keeps the date fixed from props and
  // never sets `form.date`, so the override would otherwise be sent with no date.
  singleDate?: string
): {
  payload: SavePayload;
  floorCeiling: SingleFloorCeiling | BulkFloorCeiling;
  isBulk: boolean;
} {
  const effectiveBasePrice = Number(
    form.baseRoomTypeNewPrice || data.baseRoomTypeRate
  );

  const derivedPrices = (form.roomTypes ?? [])
    .filter((room) => room.id !== data.baseRoomTypeId)
    .map((room) => {
      // Round the relative delta to match the displayed New Price
      const price = form.relativePrice
        ? Math.round((Number(room.percentage || 0) / 100) * effectiveBasePrice) +
          effectiveBasePrice
        : effectiveBasePrice + Number(room.fixed || 0);
      return { roomTypeId: room.id, price: String(price) };
    });

  const roomTypePrices = [
    { roomTypeId: data.baseRoomTypeId, price: String(effectiveBasePrice) },
    ...derivedPrices,
  ];

  const roomTypeFloorsCeilings = (form.roomTypes ?? []).reduce(
    (acc, room) => {
      const floor = Number(room.rateFloor || 0);
      const ceiling = Number(room.rateCeiling || 0);
      acc[room.id] = {
        rateFloor: floor === 0 ? null : floor,
        rateCeiling: ceiling === 0 ? null : ceiling,
      };
      return acc;
    },
    {} as SavePayload["roomTypeFloorsCeilings"]
  );

  const reduceBy = (key: "fixed" | "percentage") =>
    (form.roomTypes ?? []).reduce(
      (acc, room) => ({ ...acc, [room.id]: String(room[key] ?? 0) }),
      {} as Record<string, string>
    );

  const payload: SavePayload = {
    startDate: form.startDate,
    roomTypePrices,
    roomTypeFloorsCeilings,
    isOverride: true,
    fixedByRoom: reduceBy("fixed"),
    percentagesByRoom: reduceBy("percentage"),
    overridedByFixed: !form.relativePrice,
    currentPricesByRoom: (form.roomTypes ?? []).reduce(
      (acc, room) => ({ ...acc, [room.id]: room.rate ?? 0 }),
      {} as Record<string, number>
    ),
  };
  if (form.bulk) {
    payload.endDate = form.endDate;
    payload.excludedDates = form.excludedDates ?? [];
  }

  const floorCeiling: SingleFloorCeiling | BulkFloorCeiling = form.bulk
    ? {
        startDate: form.startDate,
        endDate: form.endDate,
        roomTypeFloorsCeilings,
        excludedDates: form.excludedDates ?? [],
      }
    : { date: form.date ?? singleDate, roomTypeFloorsCeilings };

  return { payload, floorCeiling, isBulk: form.bulk };
}
